import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../components/TrackerLayout';
import ClassOverModal from '../components/ClassOverModal';
import { useMentorSession } from '../context/TrackerContext';
import { useStudentCard } from '../context/StudentCardContext';
import {
  getStudentProgress, getMentorStudentProfiles, updateStudentPhase,
  getJuniorMentorOverview, getMentorStudentPortions, setMentorStudentPortions,
  updateStudentEmailByMentor, updateStudentPhoneByMentor, updateStudentCourseByMentor,
  COURSE_OPTIONS,
} from '../lib/api';
import type { ClassRecord, StudentProfile, CoursePhase, JuniorMentor, PortionsData, DayItem, Checkpoint, ProjectStatus } from '../lib/api';

// ── Phase config ────────────────────────────────────────────────

const PHASES: CoursePhase[] = ['First 5 Days', 'Mid Course', 'Last 10 Days'];

const PHASE_STYLE: Record<CoursePhase, { bg: string; color: string; border: string; dot: string }> = {
  'First 5 Days':  { bg: '#fef9c3', color: '#854d0e', border: '#fde047', dot: '#f59e0b' },
  'Mid Course':    { bg: '#ede9fe', color: '#5b21b6', border: '#a78bfa', dot: '#7c3aed' },
  'Last 10 Days':  { bg: '#dcfce7', color: '#15803d', border: '#86efac', dot: '#16a34a' },
};

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Completed':     { bg: '#dcfce7', color: '#15803d' },
  'Task Given':    { bg: '#fef9c3', color: '#854d0e' },
  'Not Completed': { bg: '#fee2e2', color: '#b91c1c' },
  'N/A':           { bg: '#f3f4f6', color: '#9ca3af' },
};

const DIFFICULTY_COLORS: Record<string, string> = { Easy: '#22c55e', Medium: '#F0B429', Hard: '#ef4444' };
type DetailTab = 'overview' | 'log' | 'portions';
type ContactField = 'phone' | 'email' | 'course';

// ── Avatar initials ─────────────────────────────────────────────
function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// ── Contact edit modal ─────────────────────────────────────────

const ContactModal: React.FC<{
  field: ContactField;
  student: StudentProfile;
  mentorId: string;
  onSave: (field: ContactField, value: string) => void;
  onClose: () => void;
}> = ({ field, student, mentorId, onSave, onClose }) => {
  const [val, setVal] = useState(field === 'phone' ? (student.phone || '') : field === 'email' ? (student.email || '') : (student.course || ''));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val.trim()) return;
    setSaving(true);
    try {
      if (field === 'phone') await updateStudentPhoneByMentor(mentorId, student.studentId, val.trim());
      else if (field === 'email') await updateStudentEmailByMentor(mentorId, student.studentId, val.trim());
      else await updateStudentCourseByMentor(mentorId, student.studentId, val.trim());
      onSave(field, val.trim());
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`);
      onClose();
    } catch (err: any) {
      toast.error('Failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const labels: Record<ContactField, string> = { phone: 'Phone Number', email: 'Email Address', course: 'Course' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-10">
        <h3 className="text-sm font-bold mb-1" style={{ color: '#2b2240' }}>Update {labels[field]}</h3>
        <p className="text-xs text-gray-400 mb-4">{student.name}</p>
        {field === 'course' ? (
          <select value={val} onChange={e => setVal(e.target.value)} autoFocus
            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 mb-4 bg-white">
            <option value="">— Select course —</option>
            {COURSE_OPTIONS.map(c => <option key={c.key} value={c.label}>{c.label}</option>)}
          </select>
        ) : (
          <input type={field === 'email' ? 'email' : 'tel'} value={val} onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onClose(); }}
            placeholder={field === 'phone' ? '10-digit number' : 'email@example.com'}
            autoFocus
            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 mb-4" />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !val.trim()}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-50"
            style={{ background: '#2b2240' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Portions tab ───────────────────────────────────────────────

const PortionsTab: React.FC<{ studentId: string; mentorId: string; readOnly?: boolean }> = ({ studentId, mentorId, readOnly = false }) => {
  const [data, setData]   = useState<PortionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    setLoading(true);
    getMentorStudentPortions(studentId)
      .then(res => { setData(res); setChecked(new Set(res.completedDays)); setDirty(false); })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [studentId]);

  const toggle = (day: number) => {
    if (readOnly) return;
    setChecked(prev => { const n = new Set(prev); n.has(day) ? n.delete(day) : n.add(day); return n; });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await setMentorStudentPortions(studentId, Array.from(checked), mentorId);
      toast.success('Portions saved');
      setDirty(false);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading portions…</div>;

  if (!data?.totalDays) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-14 h-14 rounded-2xl mb-4 flex items-center justify-center text-2xl" style={{ background: '#f0edf7' }}>📚</div>
        <p className="text-sm font-semibold text-gray-600">No curriculum assigned</p>
        <p className="text-xs text-gray-400 mt-1">Ask the admin to assign a curriculum from the Student Progress page.</p>
      </div>
    );
  }

  const pct = Math.round((checked.size / data.totalDays) * 100);
  const cpMap: Record<number, Checkpoint> = {};
  (data.projectCheckpoints || []).forEach(cp => { cpMap[cp.afterDay] = cp; });
  const statusMap: Record<number, ProjectStatus> = {};
  (data.projectStatuses || []).forEach(ps => { statusMap[ps.phase] = ps; });

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Progress bar header */}
      <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-0.5">{data.curriculumTitle}</p>
            <span className="text-3xl font-black" style={{ color: '#2b2240' }}>{pct}%</span>
            <span className="text-sm text-gray-400 ml-2">{checked.size} / {data.totalDays} days</span>
          </div>
          {!readOnly && dirty && (
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-3 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#F0B429,#FF6535)' }} />
        </div>
        {dirty && <p className="text-xs text-amber-600 mt-1.5 font-medium">Unsaved changes — click Save Changes</p>}
      </div>

      {/* Day checklist */}
      <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-50 px-2 py-1">
        {(data.days || []).map((d: DayItem) => {
          const isChecked = checked.has(d.day);
          const cp = cpMap[d.day];
          const ps = cp ? statusMap[cp.phase] : null;
          return (
            <React.Fragment key={d.day}>
              <label className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer select-none transition-colors ${isChecked ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={isChecked} onChange={() => toggle(d.day)} disabled={readOnly}
                  className="w-4 h-4 accent-green-500 flex-shrink-0" />
                <span className={`text-xs font-bold w-8 flex-shrink-0 ${isChecked ? 'text-green-600' : 'text-gray-400'}`}>
                  D{String(d.day).padStart(2, '0')}
                </span>
                <span className={`text-sm flex-1 min-w-0 ${isChecked ? 'text-green-800 font-medium' : 'text-gray-600'}`}>{d.topic}</span>
                {isChecked && <span className="text-green-500 text-sm">✓</span>}
              </label>
              {cp && (
                <div className="mx-3 my-0.5 px-3 py-2 rounded-xl flex items-center gap-2"
                  style={{ background: ps?.status === 'completed' ? '#f0fdf4' : ps?.status === 'available' ? '#fefce8' : '#f9fafb' }}>
                  <span className="text-sm">{ps?.status === 'completed' ? '✅' : ps?.status === 'available' ? '📋' : '🔒'}</span>
                  <span className="text-xs font-bold flex-1"
                    style={{ color: ps?.status === 'completed' ? '#166534' : ps?.status === 'available' ? '#92400e' : '#9ca3af' }}>
                    Project {cp.phase} — {cp.description}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white"
                    style={{ background: DIFFICULTY_COLORS[cp.difficulty] || '#6b7280' }}>
                    {cp.difficulty}
                  </span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ── Log tab ────────────────────────────────────────────────────

const LogTab: React.FC<{ records: ClassRecord[]; loading: boolean }> = ({ records, loading }) => {
  const [search, setSearch] = useState('');

  if (loading) return <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading class history…</div>;

  if (records.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-14 h-14 rounded-2xl mb-4 flex items-center justify-center text-2xl" style={{ background: '#f0edf7' }}>📋</div>
        <p className="text-sm font-semibold text-gray-600">No class records yet</p>
        <p className="text-xs text-gray-400 mt-1">Log the first class from the "Log a Class" page.</p>
      </div>
    );
  }

  const filtered = search
    ? records.filter(r => r.topic.toLowerCase().includes(search.toLowerCase()) || (r.portion || '').toLowerCase().includes(search.toLowerCase()))
    : records;

  const present = records.filter(r => r.attendance === 'Present').length;
  const attPct  = records.length ? Math.round((present / records.length) * 100) : 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Summary + search */}
      <div className="px-4 pt-3 pb-2 flex-shrink-0 border-b border-gray-100 space-y-2">
        <div className="flex gap-3">
          {[
            { label: 'Total', value: records.length, color: '#2b2240' },
            { label: 'Present', value: present, color: '#16a34a' },
            { label: 'Absent', value: records.length - present, color: '#dc2626' },
            { label: 'Att %', value: `${attPct}%`, color: attPct >= 80 ? '#16a34a' : attPct >= 60 ? '#d97706' : '#dc2626' },
          ].map(s => (
            <div key={s.label} className="flex-1 bg-gray-50 rounded-xl py-2 text-center border border-gray-100">
              <div className="text-base font-black" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search topic or portion…"
          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-yellow-400"
        />
      </div>

      {/* Cards */}
      <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
        {filtered.length === 0 ? (
          <p className="text-center text-xs text-gray-400 py-8">No results for "{search}"</p>
        ) : (
          filtered.map((r, idx) => {
            const sc = STATUS_STYLE[r.completionStatus] ?? { bg: '#f3f4f6', color: '#374151' };
            const isPresent = r.attendance === 'Present';
            return (
              <div key={idx} className={`px-4 py-3 ${isPresent ? '' : 'bg-red-50/30'}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold tabular-nums" style={{ color: '#2b2240' }}>{r.date}</span>
                  <span className="text-[10px] text-gray-400 font-medium">{r.day}</span>
                  <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${isPresent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {r.attendance}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                    style={{ background: sc.bg, color: sc.color }}>
                    {r.completionStatus}
                  </span>
                </div>
                {r.topic && <p className="text-sm text-gray-800 font-medium leading-snug">{r.topic}</p>}
                {r.portion && r.portion !== r.topic && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Portion · </span>{r.portion}
                  </p>
                )}
                {r.remark && <p className="text-xs text-gray-400 mt-0.5 italic">"{r.remark}"</p>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ── Overview tab ───────────────────────────────────────────────

const OverviewTab: React.FC<{
  student: StudentProfile;
  mentorId: string;
  onPhaseChange: (studentId: string, phase: CoursePhase) => void;
  onContactUpdate: (studentId: string, field: ContactField, value: string) => void;
  onClassOver: (studentId: string) => void;
  readOnly: boolean;
  records: ClassRecord[];
  loading: boolean;
}> = ({ student, mentorId, onPhaseChange, onContactUpdate, onClassOver, readOnly, records, loading }) => {
  const [updatingPhase, setUpdatingPhase] = useState(false);
  const [contactModal, setContactModal]   = useState<ContactField | null>(null);
  const [classOverOpen, setClassOverOpen] = useState(false);

  const handlePhaseChange = async (phase: CoursePhase) => {
    if (readOnly || phase === (student.phase || 'First 5 Days') || updatingPhase) return;
    setUpdatingPhase(true);
    try {
      await updateStudentPhase(mentorId, student.studentId, phase);
      toast.success(`Moved to "${phase}"`);
      onPhaseChange(student.studentId, phase);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUpdatingPhase(false);
    }
  };

  const currentPhase  = (student.phase || 'First 5 Days') as CoursePhase;
  const phaseStyle    = PHASE_STYLE[currentPhase] ?? PHASE_STYLE['First 5 Days'];
  const present       = records.filter(r => r.attendance === 'Present').length;
  const absent        = records.filter(r => r.attendance === 'Absent').length;
  const attPct        = records.length ? Math.round((present / records.length) * 100) : 0;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">

      {/* Contact card */}
      <div className="mx-4 mt-4 mb-3 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between" style={{ background: '#f8f6ff' }}>
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#2b2240' }}>Contact Info</span>
        </div>
        <div className="divide-y divide-gray-100 bg-white">
          {/* Phone */}
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f0edf7' }}>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" stroke="#2b2240" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Phone</p>
              <p className="text-sm font-semibold text-gray-800">{student.phone || <span className="text-amber-500 italic font-normal text-xs">Not set</span>}</p>
            </div>
            {!readOnly && (
              <button onClick={() => setContactModal('phone')}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 flex-shrink-0">Edit</button>
            )}
          </div>
          {/* Email */}
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f0edf7' }}>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" stroke="#2b2240" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Email</p>
              <p className="text-sm text-gray-700 truncate">{student.email || <span className="text-amber-500 italic font-normal text-xs">Not set</span>}</p>
            </div>
            {!readOnly && (
              <button onClick={() => setContactModal('email')}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 flex-shrink-0">Edit</button>
            )}
          </div>
          {/* Course */}
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f0edf7' }}>
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" stroke="#2b2240" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v6"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Course</p>
              <p className="text-sm font-medium text-gray-800 truncate">{student.course || 'Not set'}</p>
            </div>
            {!readOnly && (
              <button onClick={() => setContactModal('course')}
                className="text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 flex-shrink-0">Edit</button>
            )}
          </div>
        </div>
      </div>

      {/* Phase card */}
      <div className="mx-4 mb-3 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between" style={{ background: '#f8f6ff' }}>
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#2b2240' }}>Course Phase</span>
          {updatingPhase && <span className="text-xs text-gray-400 animate-pulse">Updating…</span>}
        </div>
        <div className="bg-white px-4 py-3">
          <div className="flex gap-2">
            {PHASES.map(p => {
              const s = PHASE_STYLE[p];
              const active = currentPhase === p;
              return (
                <button key={p} onClick={() => handlePhaseChange(p)} disabled={readOnly || updatingPhase}
                  className="flex-1 py-2.5 rounded-xl text-xs font-bold border-2 transition-all disabled:opacity-60"
                  style={active
                    ? { background: s.bg, color: s.color, borderColor: s.border }
                    : { background: '#fff', color: '#9ca3af', borderColor: '#f3f4f6' }}>
                  {p === 'First 5 Days' ? 'Day 1–5' : p === 'Mid Course' ? 'Mid Course' : 'Last 10'}
                  {active && <span className="block text-[9px] mt-0.5 opacity-70">● Current</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: phaseStyle.dot }} />
            <span className="text-xs font-semibold" style={{ color: phaseStyle.color }}>{currentPhase}</span>
          </div>
        </div>
      </div>

      {/* Attendance stats */}
      <div className="mx-4 mb-4 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 border-b border-gray-100" style={{ background: '#f8f6ff' }}>
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#2b2240' }}>Attendance</span>
        </div>
        <div className="bg-white px-4 py-3">
          {loading ? (
            <div className="h-12 flex items-center justify-center text-xs text-gray-400">Loading…</div>
          ) : (
            <>
              <div className="flex items-end justify-between mb-2">
                <span className="text-3xl font-black" style={{ color: attPct >= 80 ? '#16a34a' : attPct >= 60 ? '#d97706' : '#dc2626' }}>{attPct}%</span>
                <span className="text-xs text-gray-400 pb-1">{present} present · {absent} absent · {records.length} total</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${attPct}%`, background: attPct >= 80 ? '#16a34a' : attPct >= 60 ? '#d97706' : '#dc2626' }} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Course finished → Class Over */}
      {!readOnly && (
        <div className="mx-4 mb-4 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 border-b border-gray-100" style={{ background: '#f8f6ff' }}>
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#2b2240' }}>Course Finished?</span>
          </div>
          <div className="bg-white px-4 py-3 flex items-center gap-3 flex-wrap">
            <p className="flex-1 min-w-[180px] text-xs text-gray-500 leading-relaxed">
              Once you've taken the last class, raise Class Over — admin is notified with the class report.
            </p>
            <button onClick={() => setClassOverOpen(true)} disabled={loading}
              className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-colors hover:bg-sky-50 disabled:opacity-50"
              style={{ borderColor: '#7dd3fc', color: '#0369a1' }}>
              🎓 Raise Class Over
            </button>
          </div>
        </div>
      )}

      {/* Contact modal */}
      {contactModal && (
        <ContactModal
          field={contactModal}
          student={student}
          mentorId={mentorId}
          onSave={(f, v) => onContactUpdate(student.studentId, f, v)}
          onClose={() => setContactModal(null)}
        />
      )}

      {classOverOpen && (
        <ClassOverModal
          studentId={student.studentId}
          studentName={student.name}
          course={student.course}
          raisedBy={mentorId}
          onDone={() => { setClassOverOpen(false); onClassOver(student.studentId); }}
          onClose={() => setClassOverOpen(false)}
        />
      )}
    </div>
  );
};

// ── Student detail panel (right) ───────────────────────────────

const StudentDetail: React.FC<{
  student: StudentProfile;
  mentorId: string;
  onPhaseChange: (studentId: string, phase: CoursePhase) => void;
  onContactUpdate: (studentId: string, field: ContactField, value: string) => void;
  onClassOver: (studentId: string) => void;
  readOnly: boolean;
}> = ({ student, mentorId, onPhaseChange, onContactUpdate, onClassOver, readOnly }) => {
  const [tab, setTab]         = useState<DetailTab>('overview');
  const [records, setRecords] = useState<ClassRecord[]>([]);
  const { onStudentEnter, onStudentMove, onStudentLeave } = useStudentCard();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTab('overview');
    setLoading(true);
    setRecords([]);
    getStudentProgress(student.studentId, mentorId)
      .then(res => setRecords(res.records))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [student.studentId, mentorId]);

  const currentPhase = (student.phase || 'First 5 Days') as CoursePhase;
  const phaseStyle   = PHASE_STYLE[currentPhase];

  const TABS: { key: DetailTab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'log',      label: 'Class Log', count: loading ? undefined : records.length },
    { key: 'portions', label: 'Portions' },
  ];

  return (
    <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">

      {/* Student header */}
      <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-sm font-black flex-shrink-0"
            style={{ background: '#F0B429', color: '#2b2240' }}>
            {initials(student.name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-lg" style={{ background: 'rgba(240,180,41,0.2)', color: '#F0B429' }}>
                {student.studentId}
              </span>
              {student.matched
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-400/20 text-green-300 font-medium">Enrolled</span>
                : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 font-medium">Unmatched</span>}
              {readOnly && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-400/20 text-blue-300 font-medium">View Only</span>}
            </div>
            <p
              className="text-white font-bold mt-1 truncate cursor-default"
              onMouseEnter={e => onStudentEnter(student.studentId, student.name, e)}
              onMouseMove={onStudentMove}
              onMouseLeave={onStudentLeave}
            >{student.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: phaseStyle?.dot }} />
              <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>{currentPhase}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 flex border-b border-gray-100 px-1 pt-1">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="flex-1 py-2.5 text-xs font-bold transition-all relative"
            style={{ color: tab === t.key ? '#2b2240' : '#9ca3af' }}>
            {t.label}
            {t.count !== undefined && (
              <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ background: tab === t.key ? '#2b2240' : '#f3f4f6', color: tab === t.key ? '#fff' : '#9ca3af' }}>
                {t.count}
              </span>
            )}
            {tab === t.key && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{ background: '#F0B429' }} />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <OverviewTab
          student={student} mentorId={mentorId}
          onPhaseChange={onPhaseChange} onContactUpdate={onContactUpdate} onClassOver={onClassOver}
          readOnly={readOnly} records={records} loading={loading}
        />
      )}
      {tab === 'log' && <LogTab records={records} loading={loading} />}
      {tab === 'portions' && <PortionsTab studentId={student.studentId} mentorId={mentorId} readOnly={readOnly} />}
    </div>
  );
};

// ── Student list item (left panel) ─────────────────────────────

const StudentCard: React.FC<{
  profile: StudentProfile;
  isSelected: boolean;
  onClick: () => void;
}> = ({ profile, isSelected, onClick }) => {
  const currentPhase = (profile.phase || 'First 5 Days') as CoursePhase;
  const phaseStyle   = PHASE_STYLE[currentPhase];

  return (
    <div onClick={onClick}
      className={`px-3 py-3 border-b border-gray-100 cursor-pointer transition-all ${isSelected ? '' : 'hover:bg-gray-50'}`}
      style={isSelected ? { background: 'linear-gradient(135deg,#2b2240,#4e3a69)' } : {}}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0"
          style={isSelected
            ? { background: '#F0B429', color: '#2b2240' }
            : { background: '#f0edf7', color: '#2b2240' }}>
          {initials(profile.name)}
        </div>
        <div className="flex-1 min-w-0">
          <span className={`font-semibold text-sm truncate block ${isSelected ? 'text-white' : 'text-gray-800'}`}>{profile.name}</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[10px] font-mono ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>{profile.studentId}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${profile.matched ? '' : ''}`}
              style={isSelected
                ? { background: 'rgba(255,255,255,0.15)', color: '#fff' }
                : profile.matched
                  ? { background: '#dcfce7', color: '#15803d' }
                  : { background: '#fef9c3', color: '#854d0e' }}>
              {profile.matched ? 'Enrolled' : 'Unmatched'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: isSelected ? 'rgba(255,255,255,0.5)' : phaseStyle?.dot }} />
            <span className={`text-[10px] truncate ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
              {currentPhase === 'First 5 Days' ? 'Day 1–5' : currentPhase === 'Mid Course' ? 'Mid Course' : 'Last 10 Days'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────

const MyStudents: React.FC = () => {
  const { mentorId, mentorName, role } = useMentorSession();
  const isSenior = role === 'Senior';

  const [profiles, setProfiles] = useState<StudentProfile[]>([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState<StudentProfile | null>(null);
  const [search, setSearch]     = useState('');

  const [juniors, setJuniors]             = useState<JuniorMentor[]>([]);
  const [viewMentorId, setViewMentorId]   = useState(mentorId);
  const [viewReadOnly, setViewReadOnly]   = useState(false);

  useEffect(() => {
    if (!isSenior) return;
    getJuniorMentorOverview(mentorId)
      .then(res => setJuniors(res.juniors ?? []))
      .catch(() => {});
  }, [mentorId, isSenior]);

  const load = useCallback(async () => {
    if (viewReadOnly) return;
    setLoading(true);
    setSelected(null);
    setProfiles([]);
    try {
      const res = await getMentorStudentProfiles(viewMentorId);
      setProfiles(res.profiles ?? []);
    } catch (err: any) {
      toast.error('Failed to load students: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [viewMentorId, viewReadOnly]);

  useEffect(() => { load(); }, [load]);

  const handlePhaseChange = (studentId: string, phase: CoursePhase) => {
    setProfiles(prev => prev.map(p => p.studentId === studentId ? { ...p, phase } : p));
    setSelected(prev => prev?.studentId === studentId ? { ...prev, phase } : prev);
  };

  const handleContactUpdate = (studentId: string, field: ContactField, value: string) => {
    setProfiles(prev => prev.map(p => p.studentId === studentId ? { ...p, [field]: value } : p));
    setSelected(prev => prev?.studentId === studentId ? { ...prev, [field]: value } : prev);
  };

  // Class Over deactivates the student, so they leave the list
  const handleClassOver = (studentId: string) => {
    setProfiles(prev => prev.filter(p => p.studentId !== studentId));
    setSelected(null);
  };

  const switchView = (newMentorId: string, readOnly: boolean) => {
    if (newMentorId === viewMentorId) return;
    setViewMentorId(newMentorId);
    setViewReadOnly(readOnly);
    setSearch('');
    setSelected(null);
    if (readOnly) {
      const junior = juniors.find(j => j.mentorId === newMentorId);
      setProfiles((junior?.students ?? []).map(s => ({
        studentId: s.studentId, name: s.name, course: s.course, phase: s.phase,
        dateJoined: '', email: '', matched: false,
        phone: '', college: '', degree: '', yearOfPassing: '',
        enrollCourse: '', sessionCategory: '', paymentType: '',
      })));
      setLoading(false);
    }
  };

  const visible = profiles.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.studentId.toLowerCase().includes(search.toLowerCase()) ||
    (p.course && p.course.toLowerCase().includes(search.toLowerCase()))
  );

  const matched = profiles.filter(p => p.matched).length;
  const viewingJunior = juniors.find(j => j.mentorId === viewMentorId);

  return (
    <TrackerLayout>
      <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 65px)' }}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#2b2240' }}>My Students</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {viewReadOnly ? (viewingJunior?.mentorName ?? viewMentorId) : mentorName}
              &nbsp;·&nbsp; <span className="font-medium text-gray-700">{profiles.length} students</span>
              &nbsp;·&nbsp; <span className="text-green-600 font-medium">{matched} enrolled</span>
              {profiles.length - matched > 0 && (
                <span className="text-amber-600 font-medium"> · {profiles.length - matched} unmatched</span>
              )}
            </p>
          </div>
          <button onClick={load}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50">
            Refresh
          </button>
        </div>

        {/* Senior mentor tabs */}
        {isSenior && juniors.length > 0 && (
          <div className="flex-shrink-0 flex gap-2 flex-wrap mb-3">
            <button onClick={() => switchView(mentorId, false)}
              className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all"
              style={viewMentorId === mentorId
                ? { background: '#2b2240', color: '#F0B429', borderColor: '#2b2240' }
                : { borderColor: '#e5e7eb', color: '#6b7280', background: '#fff' }}>
              My Students
            </button>
            {juniors.map(j => (
              <button key={j.mentorId} onClick={() => switchView(j.mentorId, true)}
                className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all"
                style={viewMentorId === j.mentorId
                  ? { background: '#2b2240', color: '#F0B429', borderColor: '#2b2240' }
                  : { borderColor: '#e5e7eb', color: '#6b7280', background: '#fff' }}>
                {j.mentorName} {j.students.length > 0 && <span className="opacity-70">({j.students.length})</span>}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading students…</div>
        ) : profiles.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center text-3xl" style={{ background: '#f0edf7' }}>👥</div>
            <p className="font-semibold text-gray-600">
              {viewReadOnly ? 'No active students assigned to this mentor.' : 'No students assigned yet.'}
            </p>
            {!viewReadOnly && <p className="text-sm text-gray-400 mt-1">Ask the admin to assign students from the Students page.</p>}
          </div>
        ) : (
          <div className="flex-1 flex gap-4 min-h-0">

            {/* LEFT: Student list */}
            <div className="w-64 flex-shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
              {/* Search */}
              <div className="p-3 border-b border-gray-100 flex-shrink-0">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <input type="text" placeholder="Search students…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-[#2b2240]" />
                </div>
              </div>

              {/* Count bar */}
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between" style={{ background: '#f8f6ff' }}>
                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                  {visible.length} {visible.length === 1 ? 'student' : 'students'}
                </span>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {visible.length === 0 ? (
                  <p className="text-center text-xs text-gray-400 py-8">No students match "{search}"</p>
                ) : (
                  visible.map(p => (
                    <StudentCard key={p.studentId} profile={p}
                      isSelected={selected?.studentId === p.studentId}
                      onClick={() => setSelected(prev => prev?.studentId === p.studentId ? null : p)}
                    />
                  ))
                )}
              </div>

              {/* Footer legend */}
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <span className="flex items-center gap-1 text-[10px] text-gray-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"/>Enrolled
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"/>Unmatched
                </span>
              </div>
            </div>

            {/* RIGHT: Detail panel */}
            {selected ? (
              <StudentDetail
                key={selected.studentId}
                student={selected}
                mentorId={mentorId}
                onPhaseChange={handlePhaseChange}
                onContactUpdate={handleContactUpdate}
                onClassOver={handleClassOver}
                readOnly={viewReadOnly}
              />
            ) : (
              <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 rounded-3xl mb-5 flex items-center justify-center text-4xl"
                  style={{ background: 'linear-gradient(135deg,#f0edf7,#e8e2f5)' }}>
                  👤
                </div>
                <p className="text-lg font-bold" style={{ color: '#2b2240' }}>Select a student</p>
                <p className="text-sm text-gray-400 mt-1 max-w-xs">
                  Choose a student from the list to view their profile, class history, and course portions.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default MyStudents;
